import { Injectable } from "@angular/core";
import { RuleGeneratorService } from "./rule-generator.service";
import { GenerationHelperService } from "./generation-helper.service";
import { ArmourType, BaseTypeTier, WeaponType } from "../../models/enums";
import { EquipmentType } from "../../models/equipment-type.enum";
import { Filter, CustomRule, AdvancedConditions } from "../../models/filter-models";
import { ItemDataService } from "../item-data.service";
import { CraftingBasesGeneratorService } from "./crafting-bases-generator.service";
import { BaseType, ItemData } from "../../models/item-data-models";

export const excellentGearBaseTypes = [
  'Transfer-attuned Spirit Shield', 'Pneumatic Dagger', 'Anarchic Spiritblade', 'Void Fangs',
];

export const goodGearBaseTypes = [
  'Oscillating Sceptre', 'Accumulator Wand',
];

export const topLifeManaFlaskTypes: BaseType[] = [
  { name: 'Divine Life Flask', image: '/img/gear/flasks/lifeflask11.webp' },
  { name: 'Eternal Life Flask', image: '/img/gear/flasks/lifeflask12.webp' },
  { name: 'Divine Mana Flask', image: '/img/gear/flasks/manaflask11.webp' },
  { name: 'Eternal Mana Flask', image: '/img/gear/flasks/manaflask12.webp' },
];

export function getUtilityPlusTopLifeManaFlaskTypes(itemData: ItemData[]): BaseType[] {
  return itemData.find(i => i.itemType === 'Utility Flasks')!.baseTypes
    .concat(itemData.find(i => i.itemType === 'Tinctures')!.baseTypes)
    .concat(topLifeManaFlaskTypes);
}

@Injectable({
  providedIn: 'root'
})

export class Poe1CraftingBasesGeneratorService {
  constructor(
    private ruleFormatter: RuleGeneratorService,
    private helper: GenerationHelperService,
    private itemDataService: ItemDataService,
    private craftingBasesGenerator: CraftingBasesGeneratorService,
  ) {
  }

  public generateCraftingBaseRules(filter: Filter): string {
    const excellentWeaponsRule = filter.showExcellentWeaponCraftingBases
      ? new CustomRule({
        advancedConditions: new AdvancedConditions({ minQuality: 28, minItemLevel: filter.showWeaponCraftingBasesMinItemLevel }),
        baseTypes: this.helper.buildBaseTypesForTier(BaseTypeTier.Elite, filter.showWeaponCraftingBasesClasses),
      }) : undefined;

    const goodWeaponsRule = filter.showGoodWeaponCraftingBases
      ? new CustomRule({
        advancedConditions: new AdvancedConditions({ minQuality: 26, minItemLevel: filter.showWeaponCraftingBasesMinItemLevel }),
        baseTypes: this.helper.buildBaseTypesForTier(BaseTypeTier.Elite, filter.showWeaponCraftingBasesClasses),
      }) : undefined;

    const armourTypeClasses = filter.showArmourCraftingBasesTypes.includes(ArmourType.All) ? this.itemDataService.getAllArmourTypes() : filter.showArmourCraftingBasesTypes;

    const excellentArmourRule1MinItemLevel = filter.showArmourCraftingBasesMinItemLevel;
    const excellentArmourRule1 = filter.showExcellentArmourCraftingBases
      ? new CustomRule({
        advancedConditions: new AdvancedConditions({ minQuality: 28, minItemLevel: excellentArmourRule1MinItemLevel }),
        baseTypes: this.helper.buildBaseTypesForTier(BaseTypeTier.Elite, undefined, filter.showArmourCraftingBasesTypes),
      }) : undefined;

    const excellentArmourRule2MinItemLevel = Math.max(filter.showArmourCraftingBasesMinItemLevel, 84);
    const excellentArmourRule2 = filter.showExcellentArmourCraftingBases
      ? new CustomRule({
        advancedConditions: new AdvancedConditions({ minQuality: 26, minItemLevel: excellentArmourRule2MinItemLevel }),
        baseTypes: this.helper.buildBaseTypesForTier(BaseTypeTier.Elite, undefined, filter.showArmourCraftingBasesTypes),
      }) : undefined;

    const excellentArmourRule3MinItemLevel = Math.max(filter.showArmourCraftingBasesMinItemLevel, 86);
    const excellentArmourRule3Types = [ArmourType.BodyArmours, ArmourType.Boots, ArmourType.Shields].filter(t => filter.showArmourCraftingBasesTypes.includes(ArmourType.All) || filter.showArmourCraftingBasesTypes.includes(t));
    const excellentArmourRule3 = filter.showExcellentArmourCraftingBases && excellentArmourRule3Types.length > 0
      ? new CustomRule({
        advancedConditions: new AdvancedConditions({ minQuality: 24, minItemLevel: excellentArmourRule3MinItemLevel }),
        baseTypes: this.helper.buildBaseTypesForTier(BaseTypeTier.Elite, undefined, excellentArmourRule3Types),
      }) : undefined;

    const excellentArmourRule4MinItemLevel = Math.max(filter.showArmourCraftingBasesMinItemLevel, 85);
    const excellentArmourRule4Types = [ArmourType.Helmets, ArmourType.Gloves].filter(t => filter.showArmourCraftingBasesTypes.includes(ArmourType.All) || filter.showArmourCraftingBasesTypes.includes(t));
    const excellentArmourRule4 = filter.showExcellentArmourCraftingBases && excellentArmourRule4Types.length > 0
      ? new CustomRule({
        advancedConditions: new AdvancedConditions({ minQuality: 24, minItemLevel: excellentArmourRule4MinItemLevel }),
        baseTypes: this.helper.buildBaseTypesForTier(BaseTypeTier.Elite, undefined, excellentArmourRule4Types),
      }) : undefined;

    const goodArmourRule1 = filter.showGoodArmourCraftingBases
      ? new CustomRule({
        advancedConditions: new AdvancedConditions({ minQuality: 26, minBaseDefencePercentile: 60, minItemLevel: filter.showArmourCraftingBasesMinItemLevel }),
        baseTypes: this.helper.buildBaseTypesForTier(BaseTypeTier.Elite, undefined, filter.showArmourCraftingBasesTypes),
      }) : undefined;

    const goodArmourRule2 = filter.showGoodArmourCraftingBases
      ? new CustomRule({
        advancedConditions: new AdvancedConditions({ minQuality: 24, minBaseDefencePercentile: 75, minItemLevel: filter.showArmourCraftingBasesMinItemLevel }),
        baseTypes: this.helper.buildBaseTypesForTier(BaseTypeTier.Elite, undefined, filter.showArmourCraftingBasesTypes),
      }) : undefined;

    const goodArmourRule3 = filter.showGoodArmourCraftingBases
      ? new CustomRule({
        advancedConditions: new AdvancedConditions({ minQuality: 22, minBaseDefencePercentile: 90, minItemLevel: filter.showArmourCraftingBasesMinItemLevel }),
        baseTypes: this.helper.buildBaseTypesForTier(BaseTypeTier.Elite, undefined, filter.showArmourCraftingBasesTypes),
      }) : undefined;

    const weaponClasses = (filter.showWeaponCraftingBasesClasses.includes(WeaponType.All) ? this.itemDataService.getAllWeaponTypes() : filter.showWeaponCraftingBasesClasses)
      .map(t => `"${t}"`).join(' ');
    const armourClasses = armourTypeClasses.map(t => `"${t}"`).join(' ');
    const excellentArmourRule3Classes = excellentArmourRule3Types.map(t => `"${t}"`).join(' ');
    const excellentArmourRule4Classes = excellentArmourRule4Types.map(t => `"${t}"`).join(' ');

    const extraOptions = {
      customLine: `
  Corrupted False
  Mirrored False`,
    };

    const excellentGearBaseTypesRule = filter.showExcellentGearBaseTypes
      ? new CustomRule({
        advancedConditions: new AdvancedConditions({ minItemLevel: filter.showExcellentGearBaseTypesMinItemLevel ?? undefined }),
        baseTypes: excellentGearBaseTypes,
      }) : undefined;

    const goodGearBaseTypesRule = filter.showGoodGearBaseTypes
      ? new CustomRule({
        advancedConditions: new AdvancedConditions({ minItemLevel: filter.showGoodGearBaseTypesMinItemLevel ?? undefined }),
        baseTypes: goodGearBaseTypes,
      }) : undefined;

    const flaskClasses = '"Life Flasks" "Mana Flasks" "Hybrid Flasks" "Utility Flasks" "Tinctures"';
    const flaskBaseTypes = (!filter.showQualityFlaskTypes || filter.showQualityFlaskTypes.includes('All'))
      ? getUtilityPlusTopLifeManaFlaskTypes(this.itemDataService.getItemData()).map(b => b.name)
      : filter.showQualityFlaskTypes;
    const flaskMinILevel = filter.showQualityFlasksMinILevel ?? undefined;
    const excellentQualityFlasksMinQuality = filter.showExcellentQualityFlasksMinQuality;
    const goodQualityFlasksMinQuality = filter.showGoodQualityFlasksMinQuality;

    const excellentQualityFlasksRule = filter.showExcellentQualityFlasks
      ? new CustomRule({
        advancedConditions: new AdvancedConditions({ minQuality: excellentQualityFlasksMinQuality, minItemLevel: flaskMinILevel }),
        baseTypes: flaskBaseTypes,
      }) : undefined;

    const goodQualityFlasksRule = filter.showGoodQualityFlasks
      ? new CustomRule({
        advancedConditions: new AdvancedConditions({ minQuality: goodQualityFlasksMinQuality, minItemLevel: flaskMinILevel }),
        baseTypes: flaskBaseTypes,
      }) : undefined;

    const excellentQualityFlasksName = flaskMinILevel != null
      ? `Excellent Quality Flasks, ${excellentQualityFlasksMinQuality}+ Quality, Item Level ${flaskMinILevel}+`
      : `Excellent Quality Flasks, ${excellentQualityFlasksMinQuality}+ Quality`;
    const goodQualityFlasksName = flaskMinILevel != null
      ? `Good Quality Flasks, ${goodQualityFlasksMinQuality}+ Quality, Item Level ${flaskMinILevel}+`
      : `Good Quality Flasks, ${goodQualityFlasksMinQuality}+ Quality`;

    return (excellentWeaponsRule ? this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearExcellentQualityWeapon, excellentWeaponsRule, filter, 'Excellent Weapon Crafting Bases', { ...extraOptions, customClass: weaponClasses }) + '\n\n' : '') +
      (goodWeaponsRule ? this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearGoodQualityWeapon, goodWeaponsRule, filter, 'Good Weapon Crafting Bases', { ...extraOptions, customClass: weaponClasses }) + '\n\n' : '') +
      (excellentArmourRule1 ? this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearExcellentQualityArmour, excellentArmourRule1, filter, `Excellent Armour Crafting Bases, 28+ Quality, Item Level ${excellentArmourRule1MinItemLevel}+`, { ...extraOptions, customClass: armourClasses }) + '\n\n' : '') +
      (excellentArmourRule2 ? this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearExcellentQualityArmour, excellentArmourRule2, filter, `Excellent Armour Crafting Bases, 26+ Quality, Item Level ${excellentArmourRule2MinItemLevel}+`, { ...extraOptions, customClass: armourClasses }) + '\n\n' : '') +
      (excellentArmourRule3 ? this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearExcellentQualityArmour, excellentArmourRule3, filter, `Excellent Body Armour, Boots & Shields Crafting Bases, 24+ Quality, Item Level ${excellentArmourRule3MinItemLevel}+`, { ...extraOptions, customClass: excellentArmourRule3Classes }) + '\n\n' : '') +
      (excellentArmourRule4 ? this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearExcellentQualityArmour, excellentArmourRule4, filter, `Excellent Helmets & Gloves Crafting Bases, 24+ Quality, Item Level ${excellentArmourRule4MinItemLevel}+`, { ...extraOptions, customClass: excellentArmourRule4Classes }) + '\n\n' : '') +
      (goodArmourRule1 ? this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearGoodQualityArmour, goodArmourRule1, filter, `Good Armour Crafting Bases, 26+ Quality, 60% Base Defence, Item Level ${filter.showArmourCraftingBasesMinItemLevel}+`, { ...extraOptions, customClass: armourClasses }) + '\n\n' : '') +
      (goodArmourRule2 ? this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearGoodQualityArmour, goodArmourRule2, filter, `Good Armour Crafting Bases, 24+ Quality, 75% Base Defence, Item Level ${filter.showArmourCraftingBasesMinItemLevel}+`, { ...extraOptions, customClass: armourClasses }) + '\n\n' : '') +
      (goodArmourRule3 ? this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearGoodQualityArmour, goodArmourRule3, filter, `Good Armour Crafting Bases, 22+ Quality, 90% Base Defence, Item Level ${filter.showArmourCraftingBasesMinItemLevel}+`, { ...extraOptions, customClass: armourClasses }) + '\n\n' : '') +
      (excellentQualityFlasksRule ? this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherFlasksExcellentQuality, excellentQualityFlasksRule, filter, excellentQualityFlasksName, { customClass: flaskClasses }) + '\n\n' : '') +
      (goodQualityFlasksRule ? this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherFlasksGoodQuality, goodQualityFlasksRule, filter, goodQualityFlasksName, { customClass: flaskClasses }) + '\n\n' : '') +
      (excellentGearBaseTypesRule ? this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearExcellentBaseTypes, excellentGearBaseTypesRule, filter, 'Excellent Gear Base Types') + '\n\n' : '') +
      (goodGearBaseTypesRule ? this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearGoodBaseTypes, goodGearBaseTypesRule, filter, 'Good Gear Base Types') + '\n\n' : '') +
      this.craftingBasesGenerator.generateExcellentAndGoodJewelleryBaseTypesRules(filter);
  }
}
